import { useState, useEffect } from "react";
import { apiClient, Project } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Eye, ChevronLeft, ChevronRight, Search, RefreshCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/use-debounce";
import { useMultiProjectUpdates } from "@/hooks/use-multi-project-updates";

// Extended type for admin view
interface AdminProject extends Project {
  user_email?: string;
  is_reviewed?: boolean;
  reviewed_at?: string;
  artie_score?: any; // Consider typing this properly if used extensively
  artie_score_status?: string;
}

function ActionTooltip({ children, disabled, message }: { children: React.ReactNode, disabled: boolean, message: string }) {
  if (!disabled) return <>{children}</>;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-block cursor-not-allowed">
            {children}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{message}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function ProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize from URL params or default to 1/10
  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || 10;
  const initialSearch = searchParams.get("search") || "";
  const initialReviewStatus = searchParams.get("review_status") || "all";
  const initialReachStatus = searchParams.get("reach_status") || "all";
  const initialBrandFitStatus = searchParams.get("brand_fit_status") || "all";
  const initialProductionStatus = searchParams.get("production_status") || "all";

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [reviewStatus, setReviewStatus] = useState(initialReviewStatus);
  const [reachStatus, setReachStatus] = useState(initialReachStatus);
  const [brandFitStatus, setBrandFitStatus] = useState(initialBrandFitStatus);
  const [productionStatus, setProductionStatus] = useState(initialProductionStatus);
  const [regenerating, setRegenerating] = useState<string[]>([]);
  const debouncedSearch = useDebounce(searchTerm, 500);

  const queryClient = useQueryClient();

  // Helper to update URL params
  const updateParams = (newPage: number, newLimit: number, search: string, review: string, reach: string, brandFit: string, production: string) => {
    const params: Record<string, string> = {
      page: String(newPage),
      limit: String(newLimit)
    };
    if (search) params.search = search;
    if (review !== "all") params.review_status = review;
    if (reach !== "all") params.reach_status = reach;
    if (brandFit !== "all") params.brand_fit_status = brandFit;
    if (production !== "all") params.production_status = production;
    setSearchParams(params);
  };

  // Sync search state with URL when debounced value changes
  useEffect(() => {
    if (debouncedSearch !== initialSearch) {
      updateParams(1, limit, debouncedSearch, reviewStatus, reachStatus, brandFitStatus, productionStatus); // Reset to page 1 on search
    }
  }, [debouncedSearch]);

  // Sync filter states with URL when filters change
  useEffect(() => {
    if (reviewStatus !== initialReviewStatus || reachStatus !== initialReachStatus || brandFitStatus !== initialBrandFitStatus || productionStatus !== initialProductionStatus) {
      updateParams(1, limit, debouncedSearch, reviewStatus, reachStatus, brandFitStatus, productionStatus); // Reset to page 1 on filter
    }
  }, [reviewStatus, reachStatus, brandFitStatus, productionStatus]);


  // Use React Query for caching and pagination
  const { data, isLoading, isError } = useQuery({
    queryKey: ["adminProjects", page, limit, debouncedSearch, reviewStatus, reachStatus, brandFitStatus, productionStatus],
    queryFn: () => apiClient.getAdminProjects(page, limit, debouncedSearch, reviewStatus, reachStatus, brandFitStatus, productionStatus),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  const projects = data ? (Array.isArray(data) ? data : data.items) : [];
  const total = data && !Array.isArray(data) ? data.total : projects.length;
  const totalPages = Math.ceil(total / limit);

  const processingProjectIds = projects
    .filter((p: AdminProject) => p.status === 'processing' || p.status === 'analysis_in_progress' || p.status === 'script_uploaded')
    .map((p: AdminProject) => p._id);

  useMultiProjectUpdates(processingProjectIds, (projectId, data) => {
    if (data.status === 'Complete!' || data.status === 'analysis_complete' || data.percent === 100) {
      console.log(`Project ${projectId} finished processing, invalidating queries.`);
      queryClient.invalidateQueries({ queryKey: ["adminProjects"] });
    }
  });

  const handleReviewToggle = async (projectId: string, currentStatus: boolean) => {
    try {
      // Optimistic update using React Query cache
      queryClient.setQueryData(["adminProjects", page, limit, debouncedSearch, reviewStatus, reachStatus, brandFitStatus, productionStatus], (old: any) => {
        if (!old) return old;
        const isArray = Array.isArray(old);
        const list = isArray ? old : old.items;

        const updatedList = list.map((p: AdminProject) =>
          p._id === projectId ? { ...p, is_reviewed: !currentStatus } : p
        );

        return isArray ? updatedList : { ...old, items: updatedList };
      });

      await apiClient.toggleProjectReviewStatus(projectId, !currentStatus);
      toast.success(currentStatus ? "Project marked as unreviewed" : "Project marked as reviewed");
    } catch (error: any) {
      console.error("Failed to update status:", error);
      toast.error(error.message || "Failed to update review status");
      // Invalidate query to revert state on error
      queryClient.invalidateQueries({ queryKey: ["adminProjects", page, limit, debouncedSearch, reviewStatus, reachStatus, brandFitStatus, productionStatus] });
    }
  };

  const handleRegenerateScore = async (projectId: string) => {
    setRegenerating((prev) => [...prev, projectId]);
    try {
      const response = (await apiClient.regenerateScore(projectId)) as { message: string };
      toast.success(response.message || "Score regeneration started.");
      // Immediately invalidate the query to refetch project data
      queryClient.invalidateQueries({ queryKey: ["adminProjects"] });
    } catch (error: any) {
      console.error("Failed to regenerate score:", error);
      toast.error(error.message || "Failed to start score regeneration.");
    } finally {
      setRegenerating((prev) => prev.filter((id) => id !== projectId));
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      await apiClient.deleteProjectAdmin(projectId);
      toast.success("Project deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["adminProjects"] });
    } catch (error: any) {
      console.error("Failed to delete project:", error);
      toast.error(error.message || "Failed to delete project");
    }
  };

  if (isLoading && !data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return <div className="text-center py-10 text-red-500">Failed to load projects</div>;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] space-y-6 font-ibm overflow-hidden">
      {/* Header & Filters Container */}
      <div className="flex-none space-y-6 pb-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
            <div>
              <h1 className="text-3xl font-semibold text-foreground tracking-tight font-cabinet">
                All Projects
              </h1>
              <p className="text-muted-foreground font-ibm text-sm mt-1 max-w-sm">
                Manage and review all submitted projects.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-muted-foreground h-9 px-4 flex items-center justify-center font-ibm bg-card/60 backdrop-blur-sm">
              Total: {total}
            </Badge>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                <Search className="w-4 h-4" />
              </div>
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 bg-card/60 backdrop-blur-sm border border-border rounded-lg text-sm placeholder:text-muted-foreground text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm font-ibm h-[38px]"
              />
            </div>
            <div className="w-[180px]">
              <Select value={reviewStatus} onValueChange={setReviewStatus}>
                <SelectTrigger className="h-[38px] bg-card/60 backdrop-blur-sm border-border text-xs font-medium font-ibm">
                  <SelectValue placeholder="Review Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  <SelectItem value="pending">Pending Review</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Sub-score Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <span>Filter by Sub-scores:</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="w-[140px]">
                <Select value={reachStatus} onValueChange={setReachStatus}>
                  <SelectTrigger className="h-[36px] bg-card/60 backdrop-blur-sm border-border text-xs font-medium font-ibm">
                    <SelectValue placeholder="Reach" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Reach</SelectItem>
                    <SelectItem value="green">🟢 Green</SelectItem>
                    <SelectItem value="yellow">🟡 Yellow</SelectItem>
                    <SelectItem value="red">🔴 Red</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[140px]">
                <Select value={brandFitStatus} onValueChange={setBrandFitStatus}>
                  <SelectTrigger className="h-[36px] bg-card/60 backdrop-blur-sm border-border text-xs font-medium font-ibm">
                    <SelectValue placeholder="Brand Fit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Brand Fit</SelectItem>
                    <SelectItem value="green">🟢 Green</SelectItem>
                    <SelectItem value="yellow">🟡 Yellow</SelectItem>
                    <SelectItem value="red">🔴 Red</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[140px]">
                <Select value={productionStatus} onValueChange={setProductionStatus}>
                  <SelectTrigger className="h-[36px] bg-card/60 backdrop-blur-sm border-border text-xs font-medium font-ibm">
                    <SelectValue placeholder="Production" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Production</SelectItem>
                    <SelectItem value="green">🟢 Green</SelectItem>
                    <SelectItem value="yellow">🟡 Yellow</SelectItem>
                    <SelectItem value="red">🔴 Red</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Clear Filters Button */}
              {(reachStatus !== "all" || brandFitStatus !== "all" || productionStatus !== "all") && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setReachStatus("all");
                    setBrandFitStatus("all");
                    setProductionStatus("all");
                  }}
                  className="h-[36px] text-xs font-ibm"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Table Container */}
      <div className="flex-1 min-h-0 rounded-xl border border-border bg-card shadow-sm overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto custom-scrollbar">
          <Table className="min-w-[1000px]">
            <TableHeader className="sticky top-0 bg-card z-10 shadow-sm border-b border-border">
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="font-cabinet font-semibold text-foreground w-[220px]">Project Name</TableHead>
                <TableHead className="font-cabinet font-semibold text-foreground w-[180px]">Artie Score</TableHead>
                <TableHead className="font-cabinet font-semibold text-foreground">Created By</TableHead>
                <TableHead className="font-cabinet font-semibold text-foreground">Date</TableHead>
                <TableHead className="font-cabinet font-semibold text-foreground">Status</TableHead>
                <TableHead className="font-cabinet font-semibold text-foreground">Review Status</TableHead>
                <TableHead className="font-cabinet font-semibold text-foreground text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.map((project: AdminProject) => (
                <TableRow key={project._id} className="hover:bg-secondary/50 border-b border-border transition-colors">
                  <TableCell className="font-medium">
                    <div className="flex flex-col max-w-[220px]">
                      <span className="font-semibold text-foreground truncate" title={project.title}>{project.title}</span>
                      <span className="text-xs text-muted-foreground truncate font-ibm" title={project.logline || ""}>
                        {project.logline || "No logline"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {project.artie_score ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 font-mono">
                            {project.artie_score.artie_score}/100
                          </Badge>
                          {project.artie_score.triggered_by !== 'admin' ? (
                            <Badge variant="outline" className="text-xs">User Generated</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Admin Regenerated</Badge>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Badge
                            variant="outline"
                            className={`text-xs ${project.artie_score.reach_status === 'green'
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : project.artie_score.reach_status === 'yellow'
                                ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                : 'bg-red-50 text-red-700 border-red-200'
                              }`}
                            title="Reach Status"
                          >
                            R
                          </Badge>
                          <Badge
                            variant="outline"
                            className={`text-xs ${project.artie_score.brand_fit_status === 'green'
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : project.artie_score.brand_fit_status === 'yellow'
                                ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                : 'bg-red-50 text-red-700 border-red-200'
                              }`}
                            title="Brand Fit Status"
                          >
                            B
                          </Badge>
                          <Badge
                            variant="outline"
                            className={`text-xs ${project.artie_score.production_readiness_status === 'green'
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : project.artie_score.production_readiness_status === 'yellow'
                                ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                : 'bg-red-50 text-red-700 border-red-200'
                              }`}
                            title="Production Readiness Status"
                          >
                            P
                          </Badge>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground font-ibm">-</span>
                    )}
                  </TableCell>
                  <TableCell className="font-ibm text-sm">{project.user_email || "Unknown"}</TableCell>
                  <TableCell className="font-ibm text-sm">
                    {new Date(project.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Badge variant={project.status === 'processing' || project.status === 'analysis_in_progress' || project.status === 'script_uploaded' ? "outline" : "secondary"} className={`capitalize font-ibm ${project.status === 'processing' || project.status === 'analysis_in_progress' || project.status === 'script_uploaded' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-secondary text-secondary-foreground'}`}>
                      {project.status === 'processing' || project.status === 'analysis_in_progress' || project.status === 'script_uploaded' ? (
                        <div className="flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Analysis in progress
                        </div>
                      ) : (
                        project.status.replace(/_/g, " ")
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <ActionTooltip disabled={project.status !== 'analysis_complete'} message="Analysis must be complete to review">
                        <Switch
                          checked={!!project.is_reviewed}
                          onCheckedChange={() => handleReviewToggle(project._id, !!project.is_reviewed)}
                          disabled={project.status !== 'analysis_complete'}
                          className="data-[state=checked]:bg-primary"
                        />
                      </ActionTooltip>
                      <span className={`text-sm font-ibm ${project.is_reviewed ? "text-primary font-medium" : "text-muted-foreground"}`}>
                        {project.is_reviewed ? "Reviewed" : "Pending"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <ActionTooltip disabled={project.status === 'processing' || project.status === 'analysis_in_progress' || project.status === 'script_uploaded'} message="Cannot view while processing">
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                          className="hover:bg-primary/10 hover:text-primary transition-colors"
                          disabled={project.status === 'processing' || project.status === 'analysis_in_progress' || project.status === 'script_uploaded'}
                        >
                          <Link to={`/admin/projects/${project._id}`} className={project.status === 'processing' || project.status === 'analysis_in_progress' || project.status === 'script_uploaded' ? "pointer-events-none" : ""}>
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Link>
                        </Button>
                      </ActionTooltip>

                      <ActionTooltip disabled={project.status !== 'analysis_complete'} message="Analysis must be complete to regenerate">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRegenerateScore(project._id)}
                          disabled={regenerating.includes(project._id) || project.status !== 'analysis_complete'}
                          className="hover:bg-blue-500/10 hover:text-blue-500 transition-colors"
                        >
                          {regenerating.includes(project._id) ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <RefreshCcw className="h-4 w-4 mr-1" />
                          )}
                          Regenerate
                        </Button>
                      </ActionTooltip>

                      <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="hover:bg-red-500/10 hover:text-red-500 transition-colors"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This action cannot be undone. This will permanently delete the project
                              and all of its data.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteProject(project._id)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {projects.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center text-muted-foreground font-ibm">
                    No projects found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination Controls - Fixed at Bottom */}
      <div className="flex-none pt-4">
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium text-muted-foreground">Rows per page</p>
            <Select
              value={`${limit}`}
              onValueChange={(value) => {
                updateParams(1, Number(value), debouncedSearch, reviewStatus, reachStatus, brandFitStatus, productionStatus);
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={limit} />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 20, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <div className="flex w-[100px] items-center justify-center text-sm font-medium text-muted-foreground">
              Page {page} of {totalPages || 1}
            </div>
            <Button
              variant="outline"
              className="h-8 w-8 p-0 bg-card hover:bg-secondary border-border"
              onClick={() => updateParams(Math.max(1, page - 1), limit, debouncedSearch, reviewStatus, reachStatus, brandFitStatus, productionStatus)}
              disabled={page === 1}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0 bg-card hover:bg-secondary border-border"
              onClick={() => updateParams(Math.min(totalPages, page + 1), limit, debouncedSearch, reviewStatus, reachStatus, brandFitStatus, productionStatus)}
              disabled={page === totalPages || totalPages === 0}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
